import { Box, Heading, HStack, Text, VStack } from "components/ui";
import { Progress } from "components/ui/progress";
import { useTranslation } from "hooks/useTranslation";
import { useCallback, useEffect, useState } from "react";
import { FaSpinner } from "react-icons/fa";
import serverGenerationHubService, {
  ServerProgressUpdate,
} from "services/serverGenerationHubService";
import { serverService } from "services/serverService";
import { getThemeColor } from "theme/colors";

interface Step3GenerationProgressProps {
  serverId: string;
  onComplete: (suggestedMaskingRulesCount: number) => void;
  onError: () => void;
}

export const Step3GenerationProgress = ({
  serverId,
  onComplete,
  onError,
}: Step3GenerationProgressProps) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [serverName, setServerName] = useState("Your Server");
  const [message, setMessage] = useState(
    t("app.onboarding.step3.startingGeneration"),
  );
  const [processedModels, setProcessedModels] = useState(0);
  const [totalModels, setTotalModels] = useState(0);
  const [processedTools, setProcessedTools] = useState(0);
  const [totalTools, setTotalTools] = useState(0);
  const [toolCount, setToolCount] = useState(0);

  // Stable callback that won't change between renders
  const handleProgressUpdate = useCallback(
    (update: ServerProgressUpdate) => {
      setProgress(update.progress);
      setMessage(update.message || t("app.onboarding.step3.processing"));
      setProcessedModels(update.processedModels || 0);
      setTotalModels(update.totalModels || 0);
      setProcessedTools(update.processedTools || 0);
      setTotalTools(update.totalTools || 0);
      setToolCount(update.toolCount || 0);

      // Check if generation is complete
      if (update.progress === 100 && update.status === "Completed") {
        setTimeout(() => {
          onComplete(update.suggestedMaskingRulesCount || 0);
        }, 1000);
      }

      // Check for errors
      if (update.status === "Error") {
        onError();
      }
    },
    [onComplete, onError],
  );

  // Initialize SignalR connection and subscribe to progress
  useEffect(() => {
    let isSubscribed = true;

    const initializeAndSubscribe = async () => {
      try {
        // Ensure SignalR connection is started
        await serverGenerationHubService.start();

        if (!isSubscribed) return;

        // Load initial server state
        const server = await serverService.getServerById(serverId);
        if (server && isSubscribed) {
          setProgress(server.processingProgress || 0);
          setServerName(server.name);
          setProcessedModels(server.processedModels || 0);
          setTotalModels(server.totalModels || 0);
          setProcessedTools(server.processedTools || 0);
          setTotalTools(server.totalTools || 0);

          // If already complete, trigger completion (count unknown from poll — default 0, modal will skip if none)
          if (server.processingProgress === 100) {
            setTimeout(() => {
              if (isSubscribed) {
                onComplete(0);
              }
            }, 1000);
            return;
          }
        }

        // Subscribe to progress updates
        await serverGenerationHubService.subscribeToServer(
          serverId,
          handleProgressUpdate,
        );
      } catch (error) {
        console.error("Step3 initialization failed:", error);
        if (isSubscribed) {
          onError();
        }
      }
    };

    initializeAndSubscribe();

    // Cleanup
    return () => {
      isSubscribed = false;
      serverGenerationHubService.unsubscribeFromServer(
        serverId,
        handleProgressUpdate,
      );
    };
  }, [serverId, handleProgressUpdate, onComplete, onError]);

  const getProgressColor = () => {
    if (progress < 30) return "blue";
    if (progress < 70) return "purple";
    return "green";
  };

  return (
    <Box position="relative" h="full" display="flex" flexDirection="column">
      {/* Scrollable Content */}
      <Box flex="1" overflowY="auto" px={6} py={4}>
        <VStack gap={4} align="stretch">
          {/* Main Progress */}
          <Box>
            <HStack justify="space-between" mb={2}>
              <Text fontWeight="semibold" fontSize="md">
                {t("app.onboarding.step3.overallProgress")}
              </Text>
              <Text
                fontWeight="bold"
                fontSize="xl"
                color={`${getProgressColor()}.500`}
              >
                {progress}%
              </Text>
            </HStack>
            <Progress
              value={progress}
              colorPalette={getProgressColor()}
              size="md"
              borderRadius="full"
            />
          </Box>

          {/* Status Message */}
          <Box
            bg={getThemeColor("bg.secondary")}
            borderRadius="md"
            p={3}
            borderLeftWidth="3px"
            borderLeftColor={`${getProgressColor()}.400`}
          >
            <Text color={getThemeColor("text.primary")} fontSize="sm">
              {message}
            </Text>
          </Box>

          {/* Detailed Processing Info */}
          <Box
            borderWidth="1px"
            borderRadius="md"
            borderColor={getThemeColor("border.default")}
            p={4}
            bg={getThemeColor("bg.secondary")}
          >
            <Heading size="xs" mb={3} color={getThemeColor("text.primary")}>
              {t("app.onboarding.step3.overallProgress")}
            </Heading>

            <VStack gap={3} align="stretch">
              {/* Parse Models and Tools Step */}
              <HStack gap={2}>
                {progress < 100 && totalModels === 0 && (
                  <Box
                    as={FaSpinner}
                    color={getThemeColor("progress.fill").base}
                    animation="spin 1s linear infinite"
                    css={{
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                )}
                <Text fontSize="sm" color={getThemeColor("text.primary")}>
                  {t("app.onboarding.step3.analyzing")}
                </Text>
              </HStack>

              {/* Models Enhancement Progress */}
              {totalModels > 0 && (
                <Box w="full">
                  <HStack justify="space-between" mb={1}>
                    <Text fontSize="sm" color={getThemeColor("text.primary")}>
                      {t("app.onboarding.step3.modelsProgress")}:
                    </Text>
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color={getThemeColor("text.primary")}
                    >
                      {t("app.onboarding.step3.modelsProcessed", {
                        processed: processedModels,
                        total: totalModels,
                      })}
                    </Text>
                  </HStack>
                  <Box
                    w="full"
                    h="6px"
                    bg={getThemeColor("progress.bg")}
                    borderRadius="full"
                    overflow="hidden"
                  >
                    <Box
                      h="full"
                      bg={
                        processedModels === totalModels
                          ? getThemeColor("progress.fillComplete").base
                          : getThemeColor("progress.fill").base
                      }
                      w={`${(processedModels / totalModels) * 100}%`}
                      transition="width 0.3s ease, background-color 0.3s ease"
                    />
                  </Box>
                </Box>
              )}

              {/* Tools Processing Progress */}
              {totalTools > 0 && (
                <Box w="full">
                  <HStack justify="space-between" mb={1}>
                    <Text fontSize="sm" color={getThemeColor("text.primary")}>
                      {t("app.onboarding.step3.toolsProgress")}:
                    </Text>
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color={getThemeColor("text.primary")}
                    >
                      {t("app.onboarding.step3.toolsProcessed", {
                        processed: processedTools,
                        total: totalTools,
                      })}
                    </Text>
                  </HStack>
                  <Box
                    w="full"
                    h="6px"
                    bg={getThemeColor("progress.bg")}
                    borderRadius="full"
                    overflow="hidden"
                  >
                    <Box
                      h="full"
                      bg={
                        processedTools === totalTools
                          ? getThemeColor("progress.fillComplete").base
                          : getThemeColor("progress.fill").base
                      }
                      w={`${(processedTools / totalTools) * 100}%`}
                      transition="width 0.3s ease, background-color 0.3s ease"
                    />
                  </Box>
                </Box>
              )}

              {/* Summary Counts */}
              {toolCount > 0 && (
                <HStack
                  justify="space-between"
                  pt={2}
                  borderTopWidth="1px"
                  borderColor={getThemeColor("border.default")}
                >
                  <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                    {t("app.onboarding.step3.toolsProcessed", {
                      processed: toolCount,
                      total: toolCount,
                    })}
                    :
                  </Text>
                  <Text fontSize="sm" fontWeight="bold" color="green.600">
                    {toolCount}
                  </Text>
                </HStack>
              )}
            </VStack>
          </Box>
        </VStack>
      </Box>

      {/* Sticky Footer - Hidden during generation */}
      <Box
        position="sticky"
        bottom={0}
        borderTopWidth="1px"
        borderColor={getThemeColor("border.default")}
        bg={getThemeColor("modal.bg")}
        py={4}
        px={6}
        boxShadow="sm"
        display="none"
      >
        {/* Footer is hidden during generation - progress is automatic */}
      </Box>
    </Box>
  );
};
